/**
 * WatchButton: the primary button for one media, rendered from the server's `state` (bkfc-cms
 * media.md, "state -- the button"). First match wins:
 *
 *   watch_on set   partner name and logo, opens watch_on.url in a new tab (nothing plays in-app)
 *   BUY_NOW        BUY NOW, to /subscribe with returnTo
 *   UPCOMING       WATCH IN countdown to starts_at, not clickable; at zero it re-fetches
 *   STARTING_SOON  STARTING SOON, disabled; the state hook keeps polling
 *   anything else  WATCH NOW (LIVE adds the live badge; ENDED stays enabled, as on mobile: a
 *                  finished stream's DVR often still plays, and /play's refusal is shown if not)
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LiveBadge from '../LiveBadge';
import WatchOn from './WatchOn';
import { useMediaState } from '../../hooks/useMediaState';
import { toPaywallPath } from '../../utils/paywall';

const pad = n => String(n).padStart(2, '0');

/** Time left to `target` as {days, hours, minutes, seconds}; null once it is reached. */
export function countdownTo(target, now = Date.now()) {
  const diff = Date.parse(target) - now;
  if (!(diff > 0)) {
    return null;
  }
  const s = Math.floor(diff / 1000);
  return {
    days: pad(Math.floor(s / 86400)),
    hours: pad(Math.floor(s / 3600) % 24),
    minutes: pad(Math.floor(s / 60) % 60),
    seconds: pad(s % 60),
  };
}

/** Ticks every second while `target` is set; calls `onZero` once when it is reached. */
function useCountdown(target, onZero) {
  const [left, setLeft] = useState(() => (target ? countdownTo(target) : null));
  const onZeroRef = useRef(onZero);
  onZeroRef.current = onZero;
  useEffect(() => {
    if (!target) {
      setLeft(null);
      return undefined;
    }
    let fired = false;
    const tick = () => {
      const next = countdownTo(target);
      setLeft(next);
      if (!next && !fired) {
        fired = true;
        onZeroRef.current();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return left;
}

const BASE =
  'inline-flex items-center leading-none px-6 py-3 font-bold text-primary font-display uppercase tracking-wide';
const ENABLED = `${BASE} bg-accent hover:bg-accent-hover`;
const DISABLED = `${BASE} bg-accent/60 cursor-default`;

const PlayIcon = () => (
  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M6 4l10 6-10 6V4z" />
  </svg>
);

/**
 * @param {object} props.item a card (`cardToChannel`), event media ref or media body
 * @param {() => void} props.onPlay what WATCH NOW does (navigate to the watch page, or mount the
 *   player)
 * @param {string} [props.className] extra classes on the button
 * @param {string} [props.returnTo] where BUY NOW comes back to (default: the item's watch page)
 * @param {() => void} [props.onZero] also called when the countdown reaches zero, for an item
 *   `GET /v1/live` cannot answer for (an event card: its owner re-fetches it)
 */
const WatchButton = ({ item, onPlay, className = '', returnTo, onZero }) => {
  const navigate = useNavigate();
  const media = useMediaState(item);
  const offApp = media.watch_on;
  const counting = !offApp && media.state === 'UPCOMING' ? media.starts_at : null;
  const countdown = useCountdown(counting, () => {
    media.refresh();
    onZero?.();
  });

  if (offApp) {
    return <WatchOn watchOn={offApp} className={`${ENABLED} ${className}`} />;
  }

  if (media.state === 'BUY_NOW') {
    const back = returnTo || `/watch/${media.slug || media.id || media._id}`;
    return (
      <button
        type="button"
        className={`${ENABLED} ${className}`}
        onClick={() => navigate(toPaywallPath(back))}
      >
        Buy Now
      </button>
    );
  }

  if (media.state === 'UPCOMING') {
    return (
      <button type="button" disabled className={`${DISABLED} ${className}`}>
        <PlayIcon />
        <span className="mr-2">Watch In</span>
        {countdown && (
          <span
            className="inline-flex items-center bg-black/80 text-white px-2 py-0.5 text-sm tracking-wider rounded"
            data-testid="countdown"
          >
            {Number(countdown.days) > 0 && <>{countdown.days}:</>}
            {countdown.hours}:{countdown.minutes}:{countdown.seconds}
          </span>
        )}
      </button>
    );
  }

  if (media.state === 'STARTING_SOON') {
    return (
      <button type="button" disabled className={`${DISABLED} ${className}`}>
        Starting Soon…
      </button>
    );
  }

  return (
    <button type="button" className={`${ENABLED} ${className}`} onClick={onPlay}>
      {media.state === 'LIVE' && <LiveBadge className="mr-2" />}
      <PlayIcon />
      Watch Now
    </button>
  );
};

export default WatchButton;
